import * as React from 'react';
import type { ProposalSummaryItem } from 'shared/src/api/ai-media-plan-chat-types';

/**
 * Shared review SUMMARY block for the media-plan preview cards (Google Ads + Beeswax). Renders a
 * brief label/value overview of what will be created; each row whose annotation carries a
 * `reasoning` and/or `source` gets a collapsible "why?" toggle so the rationale is one click away
 * without cluttering the default view. Platform-agnostic — both cards render the same component.
 */
export function ProposalSummarySection({
  summary
}: {
  summary: ProposalSummaryItem[];
}): React.JSX.Element | null {
  if (!summary.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">SUMMARY</h4>
      <dl className="space-y-1 text-sm">
        {summary.map((item, i) => (
          <SummaryRow key={`summary-${i}`} item={item} />
        ))}
      </dl>
    </div>
  );
}

function SummaryRow({ item }: { item: ProposalSummaryItem }): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const hasDetail = Boolean(item.reasoning || item.source);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-gray-500">{item.label}</dt>
        <dd className="flex items-center gap-2 text-right font-medium text-gray-900">
          <span>{item.value}</span>
          {hasDetail ? (
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="text-xs font-normal text-primary hover:underline"
              aria-expanded={open}>
              {open ? 'hide' : 'why?'}
            </button>
          ) : null}
        </dd>
      </div>
      {open && hasDetail ? (
        <div className="mt-0.5 rounded bg-gray-50 px-2 py-1 text-xs text-gray-600">
          {item.reasoning ? <p>{item.reasoning}</p> : null}
          {item.source ? <p className="mt-0.5 italic">Source: {item.source}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
